/**
 * ReplyCompose — compose area for support replies and internal notes.
 *
 * Reply mode uses a TinyMCE rich-text editor (shared with the user email UI)
 * and supports file attachments via a multipart submit. Internal notes keep a
 * plain textarea and JSON submit.
 *
 * Saved replies can be inserted:
 *   1. Via the picker button (opens a searchable dropdown)
 *   2. Via shortcut: type /shortcut in the TinyMCE editor — a suggestion list
 *      appears while you type; press Enter or click to insert and replace the
 *      trigger text.
 *
 * Props:
 *   conversationId   {number}   — id of the current conversation
 *   mailboxSignature {string}   — pre-filled signature (HTML) from mailbox
 *   onSent           {function} — called after successful submit
 */

import { h, Fragment } from 'preact'
import { useState, useRef, useEffect, useCallback } from 'preact/hooks'
import { post } from '@rails/request.js'
import { SavedRepliesPicker } from './SavedRepliesPicker.jsx'
import { formatBytes } from './utils.js'
import { initTinyMCE, removeTinyMCE, syncTinyMCE } from '../../modules/email/email-tinymce.js'

const IMAGE_UPLOAD_URL = '/admin/support/image_upload'

export function ReplyCompose({ conversationId, mailboxSignature, onSent }) {
  const [mode,       setMode]       = useState(null)   // null | 'reply' | 'note'
  const [note,       setNote]       = useState('')     // plain text for note mode
  const [files,      setFiles]      = useState([])     // File[] for reply attachments
  const [sending,    setSending]    = useState(false)
  const [error,      setError]      = useState('')
  const [showPicker, setShowPicker] = useState(false)

  // Saved replies — loaded once, shared between picker and shortcut autocomplete
  const [replies,        setReplies]        = useState([])
  const [shortcutQuery,  setShortcutQuery]  = useState(null)   // null | string being typed after /
  const [shortcutIndex,  setShortcutIndex]  = useState(0)      // keyboard-selected suggestion index
  const [shortcutPos,    setShortcutPos]    = useState(null)   // { top, left } in viewport px

  const noteRef   = useRef(null)
  const fileInput = useRef(null)

  const editorId = `support_reply_editor_${conversationId}`

  // ── Load saved replies once ─────────────────────────────────────────────────
  useEffect(() => {
    fetch('/admin/support/saved_replies.json', { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setReplies(data))
      .catch(() => {})
  }, [])

  // Suggestions shown while typing /shortcut in the editor
  const shortcutSuggestions = shortcutQuery !== null
    ? replies.filter(r =>
        r.shortcut &&
        r.shortcut.toLowerCase().startsWith(shortcutQuery.toLowerCase())
      )
    : []

  // ── TinyMCE lifecycle (reply mode only) ────────────────────────────────────
  useEffect(() => {
    if (mode !== 'reply') return

    const sig = typeof mailboxSignature === 'string' ? mailboxSignature.trim() : ''
    const initial = sig ? `<br><br>${sig}` : ''

    initTinyMCE(`#${editorId}`, {
      height: 220,
      initialContent: initial,
      imagesUploadUrl: IMAGE_UPLOAD_URL,
      onImageUploadError: () => 'Kép feltöltése sikertelen',
      // Shortcut trigger: watch for /word patterns while typing
      onSetup: (editor) => {
        editor.on('input', () => {
          const text = editor.selection.getRng()?.startContainer?.textContent || ''
          const cursorPos = editor.selection.getRng()?.startOffset ?? 0
          const beforeCursor = text.slice(0, cursorPos)
          const match = beforeCursor.match(/\/(\w*)$/)
          if (match) {
            setShortcutQuery(match[1])
            setShortcutIndex(0)
            // Calculate cursor position: iframe offset + selection rect
            try {
              const iframeRect = editor.iframeElement?.getBoundingClientRect() || { top: 0, left: 0 }
              const selRng = editor.selection.getRng()
              const selRect = selRng.getBoundingClientRect()
              setShortcutPos({
                top:  iframeRect.top  + selRect.bottom + window.scrollY + 4,
                left: iframeRect.left + selRect.left   + window.scrollX,
              })
            } catch (_) {}
          } else {
            setShortcutQuery(null)
            setShortcutPos(null)
          }
        })

        editor.on('keydown', (e) => {
          if (shortcutSuggestions.length === 0) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setShortcutIndex(i => Math.min(i + 1, shortcutSuggestions.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setShortcutIndex(i => Math.max(i - 1, 0))
          } else if (e.key === 'Enter' && shortcutQuery !== null) {
            const suggestion = shortcutSuggestions[shortcutIndex]
            if (suggestion) {
              e.preventDefault()
              applyShortcut(suggestion)
            }
          } else if (e.key === 'Escape') {
            setShortcutQuery(null)
          }
        })
      },
    })

    return () => removeTinyMCE(`#${editorId}`)
  }, [mode, editorId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Apply a shortcut: replace the typed /word trigger with the reply body
  const applyShortcut = useCallback((reply) => {
    const editor = window.tinymce?.get(editorId)
    if (!editor) return

    // Delete the /shortcut trigger text before inserting
    const rng = editor.selection.getRng()
    const container = rng.startContainer
    const text = container.textContent || ''
    const offset = rng.startOffset
    const match = text.slice(0, offset).match(/\/(\w*)$/)
    if (match) {
      const triggerLen = match[0].length
      const newRng = editor.dom.createRng()
      newRng.setStart(container, offset - triggerLen)
      newRng.setEnd(container, offset)
      editor.selection.setRng(newRng)
    }
    editor.insertContent(reply.body_html || '')
    editor.save()
    setShortcutQuery(null)
    setShortcutPos(null)
  }, [editorId])

  function openReply() { setMode('reply') }

  function openNote() {
    setMode('note')
    setNote('')
    requestAnimationFrame(() => noteRef.current?.focus())
  }

  function cancel() {
    if (mode === 'reply') removeTinyMCE(`#${editorId}`)
    setMode(null)
    setNote('')
    setFiles([])
    setError('')
    setShowPicker(false)
    setShortcutQuery(null)
    setShortcutPos(null)
  }

  function switchToReply() {
    if (mode === 'reply') return
    setMode('reply')
  }

  function switchToNote() {
    if (mode === 'note') return
    removeTinyMCE(`#${editorId}`)
    setMode('note')
    setNote('')
    requestAnimationFrame(() => noteRef.current?.focus())
  }

  // ── Attachments ─────────────────────────────────────────────────────────────
  function handleFilePick(e) {
    const picked = Array.from(e.target.files || [])
    if (picked.length) setFiles(prev => [...prev, ...picked])
    e.target.value = ''
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx))
  }

  // ── Saved replies picker ───────────────────────────────────────────────────
  function insertSavedReply(replyBody) {
    if (mode === 'reply') {
      const editor = window.tinymce?.get(editorId)
      if (editor) {
        editor.insertContent(replyBody)
        editor.save()
      }
    } else {
      const ta = noteRef.current
      if (!ta) { setNote(prev => replyBody + '\n' + prev); setShowPicker(false); return }
      const start = ta.selectionStart
      const end   = ta.selectionEnd
      const next  = note.slice(0, start) + replyBody + note.slice(end)
      setNote(next)
      requestAnimationFrame(() => {
        ta.focus()
        ta.setSelectionRange(start + replyBody.length, start + replyBody.length)
      })
    }
    setShowPicker(false)
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  async function submitReply() {
    syncTinyMCE(editorId)
    const bodyHtml = window.tinymce?.get(editorId)?.getContent() || ''
    const plain    = bodyHtml.replace(/<[^>]+>/g, '').trim()
    if (!plain && files.length === 0) return

    setSending(true)
    setError('')

    const fd = new FormData()
    fd.append('body_html', bodyHtml)
    files.forEach(f => fd.append('files[]', f, f.name))

    try {
      const res = await post(`/admin/support/conversations/${conversationId}/reply`, {
        body: fd,
        responseKind: 'json',
      })
      if (res.ok) {
        const data = await res.json
        cancel()
        onSent?.(data)
      } else {
        setError('Küldés sikertelen. Kérjük, próbálja újra.')
      }
    } catch (err) {
      setError('Hálózati hiba.')
      console.error('[ReplyCompose] reply error', err)
    } finally {
      setSending(false)
    }
  }

  async function submitNote() {
    if (!note.trim()) return
    setSending(true)
    setError('')

    try {
      const res = await post(`/admin/support/conversations/${conversationId}/internal_note`, {
        body: JSON.stringify({ body: note }),
        contentType: 'application/json',
        responseKind: 'json',
      })
      if (res.ok) {
        const data = await res.json
        cancel()
        onSent?.(data)
      } else {
        setError('Mentés sikertelen. Kérjük, próbálja újra.')
      }
    } catch (err) {
      setError('Hálózati hiba.')
      console.error('[ReplyCompose] note error', err)
    } finally {
      setSending(false)
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (sending) return
    if (mode === 'note') submitNote()
    else submitReply()
  }

  const isNote = mode === 'note'

  // ── Collapsed state ────────────────────────────────────────────────────────
  if (!mode) {
    return (
      <div class="reply-area">
        <div class="reply-collapsed">
          <button type="button" class="reply-collapsed-input" onClick={openReply}>
            <i class="bi bi-reply me-2" />
            Válasz írása...
          </button>
          <button type="button" class="reply-note-btn" title="Belső megjegyzés" onClick={openNote}>
            <i class="bi bi-sticky" />
            Megjegyzés
          </button>
        </div>
      </div>
    )
  }

  // ── Expanded compose area ──────────────────────────────────────────────────
  return (
    <div class={`reply-area${isNote ? ' note-mode' : ''}`}>
      <div class="reply-expanded">

        {/* Mode tabs */}
        <div class="reply-mode-tabs">
          <button type="button" class={`reply-mode-tab${!isNote ? ' active' : ''}`} onClick={switchToReply}>
            <i class="bi bi-reply me-1" />Válasz
          </button>
          <button type="button" class={`reply-mode-tab${isNote ? ' active note-active' : ''}`} onClick={switchToNote}>
            <i class="bi bi-sticky me-1" />Megjegyzés
          </button>
        </div>

        {/* Error */}
        {error && (
          <div class="reply-error">
            <i class="bi bi-exclamation-triangle-fill" />
            {error}
            <button type="button" class="reply-error-close" onClick={() => setError('')} aria-label="Bezárás">×</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div class="reply-compose-body">
            {isNote
              ? (
                <textarea
                  ref={noteRef}
                  class="note-mode"
                  placeholder="Belső megjegyzés szövege..."
                  rows={4}
                  value={note}
                  onInput={e => setNote(e.target.value)}
                  required
                />
              )
              : (
                <textarea id={editorId} class="tinymce reply-tinymce" placeholder="Válasz szövege..." />
              )
            }

            {/* Shortcut autocomplete — fixed to cursor position inside TinyMCE iframe */}
            {!isNote && shortcutSuggestions.length > 0 && shortcutPos && (
              <div
                class="shortcut-suggestions"
                style={{ position: 'fixed', top: shortcutPos.top, left: shortcutPos.left, zIndex: 9999 }}
              >
                <div class="shortcut-suggestions-header">
                  <i class="bi bi-lightning-charge-fill me-1" />
                  Elmentett válaszok
                </div>
                {shortcutSuggestions.map((r, idx) => (
                  <div
                    key={r.id}
                    class={`shortcut-suggestion-item${idx === shortcutIndex ? ' active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); applyShortcut(r) }}
                    onMouseEnter={() => setShortcutIndex(idx)}
                  >
                    <span class="shortcut-suggestion-name">{r.name}</span>
                    {r.shortcut && <code class="shortcut-suggestion-code">/{r.shortcut}</code>}
                    <span class="shortcut-suggestion-preview">{(r.body_text || '').slice(0, 60)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachment list (reply mode only) */}
          {!isNote && files.length > 0 && (
            <ul class="reply-attachments-list">
              {files.map((file, idx) => (
                <li key={`${file.name}-${idx}`} class="reply-attachment-item">
                  <i class="bi bi-paperclip reply-attachment-icon" />
                  <span class="reply-attachment-name">{file.name}</span>
                  <span class="reply-attachment-size">{formatBytes(file.size)}</span>
                  <button type="button" class="reply-attachment-remove" title="Eltávolítás" onClick={() => removeFile(idx)}>
                    <i class="bi bi-x" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div class="reply-toolbar">
            <div class="reply-toolbar-left">
              {!isNote && (
                <Fragment>
                  <button type="button" class="reply-attach-btn" title="Fájl csatolása" onClick={() => fileInput.current?.click()}>
                    <i class="bi bi-paperclip" />Csatolás
                  </button>
                  <input ref={fileInput} type="file" multiple class="d-none" onChange={handleFilePick} />
                </Fragment>
              )}

              {!isNote && (
                <div class="reply-saved-btn-wrapper">
                  <button type="button" class="reply-saved-btn" onClick={() => setShowPicker(v => !v)}>
                    <i class="bi bi-chat-quote" />Elmentett válasz
                  </button>
                  {showPicker && (
                    <div class="reply-saved-picker-anchor">
                      <SavedRepliesPicker
                        replies={replies}
                        onSelect={insertSavedReply}
                        onClose={() => setShowPicker(false)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div class="reply-toolbar-right">
              <button type="button" class="reply-cancel-btn" onClick={cancel}>Mégse</button>
              <button
                type="submit"
                class={`reply-send-btn${isNote ? ' note-mode' : ''}`}
                disabled={sending || (isNote && !note.trim())}
              >
                {sending
                  ? <span class="spinner-border spinner-border-sm support-spinner-sm" role="status" />
                  : <i class={isNote ? 'bi bi-sticky' : 'bi bi-send'} />
                }
                {isNote ? 'Mentés' : 'Küldés'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
